import { AfterViewInit, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { TableCellComponent } from './table-cell/table-cell.component';
import { PaginatorComponent } from './paginator/paginator.component';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TableColumn } from '@nagel-types';
import { defaultPageSizes } from '@nagel-consts';
import { isNullOrUndefined } from '@nagel-utils';
import { MapCellPipe } from './map-cell-pipe';

const SORT_AFTER = 1;
const SORT_BEFORE = -1;
const KEEP_ORDER = 0;

@Component({
    selector: 'lib-table',
    imports: [
        MatTableModule,
        MatPaginatorModule,
        MatSortModule,
        MatIconModule,
        MatButtonModule,
        MatMenuModule,
        MatSidenavModule,
        MatListModule,
        MatCheckboxModule,
        TableCellComponent,
        PaginatorComponent,
        DragDropModule,
        MatProgressSpinner,
        MapCellPipe
    ],
    templateUrl: './table.component.html',
    styleUrl: './table.component.scss'
})
export class TableComponent<T> implements AfterViewInit {
    @Input() dataSource!: MatTableDataSource<T>;
    @Input() displayedColumns!: TableColumn[];
    @Input() totalItems!: number;
    @Input() pageSize!: number;
    @Input() pageIndex!: number;
    @Input() tableIsLoading?: boolean = false;

    @Output() pageChange = new EventEmitter<PageEvent>();
    @Output() sortChange = new EventEmitter<Sort>();
    @Output() selectRow = new EventEmitter<T>();
    @Output() columnOrderChange = new EventEmitter<string[]>();

    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;

    pageSizes: number[] = defaultPageSizes;

    ngAfterViewInit() {
        if (this.dataSource) {
            this.dataSource.paginator = this.paginator;
            this.dataSource.sort = this.sort;
            this.dataSource.sortData = this.sortTableData;
        }
    }

    sortTableData = (data: T[], sort: Sort): T[] => {
        const { active, direction } = sort;

        return sort.direction
            ? data.sort((a, b) => {
                const valueA = this.dataSource.sortingDataAccessor(a, active);
                const valueB = this.dataSource.sortingDataAccessor(b, active);
                const isAsc = direction === 'asc';

                let result = KEEP_ORDER;

                if (isNullOrUndefined(valueA) && isNullOrUndefined(valueB)) {
                    result = KEEP_ORDER;
                } else if (isNullOrUndefined(valueA)) {
                    result = isAsc ? SORT_AFTER : SORT_BEFORE;
                } else if (isNullOrUndefined(valueB)) {
                    result = isAsc ? SORT_BEFORE : SORT_AFTER;
                }

                return result;
            })
            : data;
    };

    getDisplayedColumns(): string[] {
        return this.displayedColumns.map((col) => col.columnDef);
    }

    onPageChange(event: PageEvent) {
        this.pageChange.emit(event);
    }

    onSortChange(event: Sort) {
        this.sortChange.emit(event);
    }

    onSelectRow(row: T) {
        this.selectRow.emit(row);
    }

    dropColumn(event: CdkDragDrop<string[]>) {
        moveItemInArray(this.displayedColumns, event.previousIndex, event.currentIndex);
        this.columnOrderChange.emit(this.displayedColumns.map((col) => col.columnDef));
    }

    getIsInEmptyState(): boolean {
        return this.totalItems === 0;
    }
}